import { TeacherPhotoReleaseFormDialogComponent } from './teacher-photo-release-form-dialog/teacher-photo-release-form-dialog.component';
import { UserDocumentType } from 'src/app/models/user/user-document/user-document-type.enum';
import { TeacherCertificationService } from 'src/app/services/teacher-certification.service';
import { TeacherCertification } from 'src/app/models/management/teacher-certification.model';
import { UserDocument } from 'src/app/models/user/user-document/user-document.model';
import { BaseComponentService } from 'src/app/services/_app/base-component.service';
import { UserDocumentsService } from 'src/app/services/user-documents.service';
import { AgreementService } from 'src/app/services/agreement.service';
import { Agreement } from 'src/app/models/management/agreement.model';
import { ApiError } from 'src/app/models/api/api-error.model';
import { AuthService } from 'src/app/services/auth.service';
import { API } from 'src/app/constants/api';
import {
  Input,
  inject,
  Component,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

@Component({
  selector: 'app-user-agreements-data',
  templateUrl: './user-agreements-data.component.html',
})
export class UserAgreementsDataComponent implements OnChanges {
  private _agreementService: AgreementService = inject(AgreementService);
  private _teacherCertificationService = inject(TeacherCertificationService);
  private _userDocumentsService = inject(UserDocumentsService);
  private _baseComponentService = inject(BaseComponentService);
  private _authService: AuthService = inject(AuthService);
  @Input() enableTeacherCertificationSection: boolean = false;
  @Input() startLoading: boolean = false;
  @Input() userId?: string;

  protected sectionsState$ = this._authService.sectionsState$;

  protected agreementsListing: Agreement[] = [];
  protected agreementsLoading: boolean = true;
  protected agreementsError?: ApiError;

  protected teacherCertification?: TeacherCertification;
  protected teacherCertificationLoading: boolean = true;
  protected teacherCertificationError?: ApiError;

  protected teacherPhotoRelease: UserDocument[] = [];
  protected teacherPhotoReleaseLoading: boolean = true;
  protected teacherPhotoReleaseError?: ApiError;

  protected get teacherCertificationSubjects(): string | undefined {
    return (
      this.teacherCertification?.teaching_subjects
        ?.map((subject) => subject.nome)
        .join(', ') || undefined
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['startLoading'].currentValue &&
      !changes['startLoading'].previousValue
    ) {
      this._init();
    }
  }

  private async _init() {
    if (this.enableTeacherCertificationSection === true) {
      await this._getTeacherCertificationData();
      await this._getTeacherPhotoReleaseFormData();
      await this._getAgreementsData();
    } else await this._getAgreementsData();
  }

  // TEACHER-CERTIFICATION
  private async _getTeacherCertificationData() {
    this.teacherCertificationLoading = true;
    this.teacherCertificationError = undefined;
    this._teacherCertificationService
      .getTeacherCertification(this.userId)
      .subscribe({
        next: (value) => (this.teacherCertification = value.data),
        error: (error: ApiError) => {
          if (error.code !== 404) this.teacherCertificationError = error;
          else {
            console.warn('GetTeacherCertification: 404');
            this.teacherCertification = undefined;
          }
        },
      })
      .add(() => (this.teacherCertificationLoading = false));
  }

  protected showTeacherCertificationDialog(teacherCertificationId?: string) {
    this._teacherCertificationService
      .openTeacherCertificationDialog({
        teacherCertificationId,
        teacherId: this.userId,
      })
      .afterClosed()
      .subscribe((response) => {
        if (response === true) this._getTeacherCertificationData();
      });
  }

  protected showTeacherCertificationDeleteDialog() {
    if (!this.teacherCertification) return;
    this._baseComponentService
      .confirmDeletion({
        message: 'Confermi di voler eliminare questa abilitazione?',
        details:
          "Attenzione: verrà eliminato anche l'eventuale allegato e nessuna operazione sarà reversibile.",
        trueBtn: 'Sì, elimina',
        falseBtn: 'No, annulla',
        destructive: true,
      })
      .subscribe((response) => {
        if (response === true) {
          this._teacherCertificationService
            .deleteTeacherCertification(this.teacherCertification?.id)
            .subscribe({
              next: (_) => this._getTeacherCertificationData(),
              error: (error) => {
                this._baseComponentService.showErrorDialog({ error });
                console.error('Delete Teacher Certification Error:', error);
              },
            });
        }
      });
  }

  protected downloadTeacherCertificationAttachment() {
    if (!this.teacherCertification) return;
    this._downloadAttachment(
      API.teacherCertificationAttachment(
        this.teacherCertification.id,
        this.teacherCertification.attachment
      )
    );
  }

  protected deleteTeacherCertificationAttachment() {
    if (!this.teacherCertification) return;
    this._baseComponentService
      .confirmDeletion({
        message: "Confermi di voler eliminare l'allegato dell'abilitazione?",
        details: "Attenzione: l'operazione non sarà reversibile",
        trueBtn: 'Sì, elimina',
        falseBtn: 'No, annulla',
        destructive: true,
      })
      .subscribe((response) => {
        if (response === true) {
          this._teacherCertificationService
            .deleteTeacherCertificationAttachment(this.teacherCertification)
            .subscribe({
              next: (_) => this._getTeacherCertificationData(),
              error: (error) => {
                this._baseComponentService.showErrorDialog({ error });
                console.error(
                  'Delete Teacher Certification Attachment Error:',
                  error
                );
              },
            });
        }
      });
  }

  // TEACHER-RELEASE-FORM
  private async _getTeacherPhotoReleaseFormData(setLoading = true) {
    this.teacherPhotoReleaseLoading = setLoading;
    this.teacherPhotoReleaseError = undefined;
    this._userDocumentsService
      .getDocumentsListing(this.userId, [UserDocumentType.PHOTO_RELEASE_FORM])
      .subscribe({
        next: (response) => {
          this.teacherPhotoRelease = (response.data ?? []).map((json: any) =>
            UserDocument.fromJson(json)
          );
        },
        error: (error) => {
          error = new ApiError(error);
          this.teacherPhotoRelease = [];
          if (error.code == 404) {
            console.warn('getTeacherPhotoReleaseForm: 404');
          } else this.teacherPhotoReleaseError = error;
        },
      })
      .add(() => (this.teacherPhotoReleaseLoading = false));
  }

  protected onChangeCheckbox(event: any, document?: UserDocument) {
    if (!document) return;
    this._userDocumentsService
      .setDocumentCheckbox(document.owner, document?.id, event.checked)
      .subscribe({
        next: (data) => console.info('DATA =>', data),
        error: (error) => console.warn('setDocumentCheckbox - ERROR:', error),
      })
      .add(() => this._getTeacherPhotoReleaseFormData(false));
  }

  protected showTeacherPhotoReleaseFormDialog() {
    const uploadDialogRef = this._baseComponentService.dialog.open(
      TeacherPhotoReleaseFormDialogComponent,
      {
        maxWidth: '480px',
        minWidth: '480px',
        data: { userId: this.userId },
        restoreFocus: false,
        autoFocus: false,
      }
    );
    uploadDialogRef.afterClosed().subscribe((dialogResult: any) => {
      if (dialogResult === true) this._getTeacherPhotoReleaseFormData();
    });
  }

  protected showTeacherPhotoReleaseDeleteDialog(document?: UserDocument) {
    if (!document || this.teacherPhotoRelease.length == 0) return;
    this._baseComponentService
      .confirmDeletion({
        message: 'Confermi di voler eliminare la liberatoria per le immagini?',
        details: "Attenzione: l'operazione non sarà reversibile.",
        trueBtn: 'Sì, elimina',
        falseBtn: 'No, annulla',
        destructive: true,
      })
      .subscribe((response) => {
        if (response === true) {
          this._userDocumentsService
            .deleteDocument(document.owner, document.id)
            .subscribe({
              next: (_) => this._getTeacherPhotoReleaseFormData(),
              error: (error) => {
                console.error('Delete Teacher Photo Release Error:', error);
                this._baseComponentService.showErrorDialog({
                  error: new ApiError(error),
                });
              },
            });
        }
      });
  }

  protected downloadTeacherPhotoReleaseFormAttachment(document?: UserDocument) {
    if (!document) return;
    this._downloadAttachment(
      API.userDocumentDownload(document.owner, document.id),
      document.nome
    );
  }

  // AGREEMENTS
  private async _getAgreementsData() {
    this.agreementsLoading = true;
    this.agreementsError = undefined;
    this._agreementService
      .getAgreementsListing({ userId: this.userId })
      .subscribe({
        next: (value) => (this.agreementsListing = value.data ?? []),
        error: (error: ApiError) => (this.agreementsError = error),
      })
      .add(() => (this.agreementsLoading = false));
  }

  protected showAgreementDialog(agreementId?: string) {
    this._agreementService
      .openAgreementDialog({ agreementId, userId: this.userId })
      .afterClosed()
      .subscribe((response) => {
        if (response === true) this._getAgreementsData();
      });
  }

  protected showAgreementDeleteDialog(id?: string) {
    if (!id) return;
    this._baseComponentService
      .confirmDeletion({
        message: 'Confermi di voler eliminare questo contratto?',
        details:
          "Attenzione: verranno eliminati anche eventuali allegati e l'operazione non sarà reversibile.",
        trueBtn: 'Sì, elimina',
        falseBtn: 'No, annulla',
        destructive: true,
      })
      .subscribe((response) => {
        if (response === true) {
          this._agreementService.deleteAgreement(id).subscribe({
            next: (_) => this._getAgreementsData(),
            error: (error) => {
              this._baseComponentService.showErrorDialog({ error });
              console.error('Delete Agreement Error:', error);
            },
          });
        }
      });
  }

  protected downloadAgreementAttachment(agreement?: Agreement) {
    if (!agreement) return;
    this._downloadAttachment(
      API.agreementAttachment(agreement.id, agreement.attachment)
    );
  }

  protected deleteAgreementAttachment(agreement?: Agreement) {
    if (!agreement) return;
    this._baseComponentService
      .confirmDeletion({
        message: "Confermi di voler eliminare l'allegato di questo contratto?",
        details: "Attenzione: l'operazione non sarà reversibile",
        trueBtn: 'Sì, elimina',
        falseBtn: 'No, annulla',
        destructive: true,
      })
      .subscribe((response) => {
        if (response === true) {
          this._agreementService
            .deleteAgreementAttachment(agreement)
            .subscribe({
              next: (_) => this._getAgreementsData(),
              error: (error) => {
                this._baseComponentService.showErrorDialog({ error });
                console.error('Delete Agreement Attachment Error:', error);
              },
            });
        }
      });
  }

  // Commons
  private _downloadAttachment(url?: string, defaultFileName?: string) {
    if (!url) return;
    this._baseComponentService.downloadFile({
      url,
      defaultFileName,
      withCredentials: true,
    });
  }
}
